import { I18n } from "config/config-general";
import { TCountryCode } from "countries-list";

// Check that number is not nan or infinite or undefined, etc.
export function isValidNumber(n: number): boolean {
  return typeof n === "number" && n !== null && !isNaN(n) && isFinite(n);
}

// Converts numbers to danish styled numbers with thousand seperator: .
export const beautifyNumber = (n: number, decimals?: number, countryCode?: TCountryCode) => {
  if (typeof n !== "number" || n === undefined || n === null) return "";
  const parts = n.toFixed(decimals ? decimals : 0).split(".");
  const numberPart = parts[0];
  const decimalPart = parts[1];
  const thousands = /\B(?=(\d{3})+(?!\d))/g;
  if (!countryCode || countryCode === "DK")
    return numberPart.replace(thousands, ".") + (decimalPart ? "," + decimalPart : "");
  return numberPart.replace(thousands, ",") + (decimalPart ? "." + decimalPart : "");
};

/** Parses a number formatted with either Danish or international thousand/decimal separators. */
export const parseLocalizedNumber = (value: string): number => {
  const input = value.trim().replace(/\s/g, "");
  if (!input) return NaN;

  const lastComma = input.lastIndexOf(",");
  const lastDot = input.lastIndexOf(".");
  let normalized = input;

  if (lastComma !== -1 && lastDot !== -1) {
    // The final separator is the decimal separator; the other is a thousands separator.
    normalized = lastComma > lastDot ? input.replace(/\./g, "").replace(",", ".") : input.replace(/,/g, "");
  } else if (lastComma !== -1) {
    normalized = input.replace(",", ".");
  } else if (/^\d{1,3}(\.\d{3})+$/.test(input)) {
    // A dot-only Danish thousands-formatted integer, e.g. "5.000".
    normalized = input.replace(/\./g, "");
  }

  return Number(normalized);
};

export const checkForValidNumber = (
  value: string,
  type?: "integer" | "float",
  fallBack?: "set-to-zero" | "set-to-empty-string" | "set-to-null",
  precision?: number
): number | string | null => {
  let val: number | string | null = NaN;
  switch (type) {
    case "integer":
      val = parseInt(value);
      break;
    case "float":
      val = roundWithPrecision(parseFloat(value), precision ? precision : 0);
      break;
    default:
      val = roundWithPrecision(parseFloat(value), precision ? precision : 0);
  }
  if (isNaN(val)) {
    switch (fallBack) {
      case "set-to-zero":
        val = 0;
        break;
      case "set-to-empty-string":
        val = "";
        break;
      case "set-to-null":
        val = null;
        break;
      default:
        val = 0;
    }
  }
  return val;
};

export function roundWithPrecision(num: number, precision: number): number {
  var multiplier = Math.pow(10, precision);
  return Math.round(num * multiplier) / multiplier;
}

export function strInputNumberCorrection(
  value: string,
  language?: I18n,
  maxDecimals?: number
): string {
  const notAcceptedChars = ["e", "-", "+"];
  let newVal = value;
  // Replace "." with "," if Danish
  const isCommaFormat = language === "da";
  const decimalSeparator = isCommaFormat ? "," : ".";

  if (isCommaFormat) {
    newVal = newVal.replace(".", ",");
  } else {
    newVal = newVal.replace(",", ".");
  }
  // Disallow last char if not valid
  const lastChar = newVal.slice(-1);
  if (notAcceptedChars.includes(lastChar)) {
    return newVal.slice(0, -1);
  }
  // Build regex: e.g. /^[0-9]*[,.]?[0-9]{0,2}$/
  const regex = new RegExp(`^[0-9]*[${decimalSeparator}]?[0-9]{0,${maxDecimals}}$`);
  if (!regex.test(newVal)) {
    return newVal.slice(0, -1);
  }
  return newVal;
}
